import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import { CalendarIcon, ChevronDownIcon } from "../Dashboard/icons";
import { ExportIcon } from "../Conversations/icons";
import { RobotIcon } from "../Auth/onboarding/icons";
import {
  getConversationTimeseries,
  getConversationTopics,
} from "../../actions/assistants";
import { readSeries, readTopics } from "./mapAnalytics";
import StatCards from "./StatCards";
import ConversationsChart from "./ConversationsChart";
import VisitorsDonut from "./VisitorsDonut";
import TopTopicsCard from "./TopTopicsCard";
import LeadsChart from "./LeadsChart";
import UserOutcomesCard from "./UserOutcomesCard";
import InsightBanner from "./InsightBanner";

const RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
];

// The API takes plain YYYY-MM-DD, so this stays in local time rather than
// going through toISOString() and shifting the day across a timezone.
const toApiDate = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const shiftDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const rangeFor = (days) => {
  const to = new Date();
  return { from: toApiDate(shiftDays(to, -(days - 1))), to: toApiDate(to) };
};

const prettyDate = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const prettyRange = ({ from, to }) => `${prettyDate(from)} – ${prettyDate(to)}`;

export default function Analytics() {
  const assistantId = useSelector((state) => state.assistants.activeId);
  const dispatch = useDispatch();

  const [days, setDays] = useState(RANGES[0].days);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [series, setSeries] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const rangeRef = useRef(null);

  const range = rangeFor(days);
  const { from, to } = range;

  useEffect(() => {
    if (!rangeOpen) return undefined;

    const onPointerDown = (event) => {
      if (!rangeRef.current?.contains(event.target)) setRangeOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [rangeOpen]);

  // Keyed on the date strings rather than the range object, so a re-render
  // with an identical window doesn't refetch.
  const load = useCallback(async () => {
    if (!assistantId) return;

    setLoading(true);
    setError("");

    // The chart shows this window split by source, so the preceding window is
    // no longer fetched - there is no comparison line to draw it on.
    const [current, topicsResult] = await Promise.all([
      dispatch(getConversationTimeseries(assistantId, { from, to })),
      dispatch(getConversationTopics(assistantId, { from, to })),
    ]);

    setLoading(false);

    setSeries(readSeries(current?.series));
    setTopics(readTopics(topicsResult?.topics));

    if (!current?.success || !topicsResult?.success) {
      setError(
        current?.message ||
          topicsResult?.message ||
          "We couldn't load your analytics.",
      );
    }
  }, [assistantId, from, to, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const renderBody = () => {
    if (!assistantId) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No assistant selected</strong>
          <p>Pick one from the switcher at the top of the sidebar.</p>
        </div>
      );
    }

    return (
      <>
        {error && <p className="auth-error">{error}</p>}

        <StatCards />

        <div className="dash-grid-primary">
          <ConversationsChart series={series} loading={loading} />
          <VisitorsDonut />
        </div>

        <div className="an-grid-3">
          <TopTopicsCard topics={topics} loading={loading} />
          <LeadsChart />
          <UserOutcomesCard />
        </div>

        <InsightBanner />
      </>
    );
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar title="Analytics" subtitle="Track performance and insights about your AI assistant.">
          <div className="dash-range-dropdown" ref={rangeRef}>
            <button
              type="button"
              className="dash-topbar__range"
              onClick={() => setRangeOpen((open) => !open)}
              aria-expanded={rangeOpen}
            >
              <CalendarIcon />
              {prettyRange(range)}
              <ChevronDownIcon />
            </button>

            {rangeOpen && (
              <ul className="dash-range-menu">
                {RANGES.map((option) => (
                  <li key={option.days}>
                    <button
                      type="button"
                      className={option.days === days ? "is-active" : ""}
                      onClick={() => {
                        setDays(option.days);
                        setRangeOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* <button type="button" className="dash-topbar__share">
            <ExportIcon />
            Export
          </button> */}
        </Topbar>

        <div className="dash-content">{renderBody()}</div>
      </div>
    </div>
  );
}
